import { useConfirm } from "@/components/DialogProvider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useDeletedWorkUnits } from "@/features/work-units/hooks/useDeletedWorkUnits";
import { formatTimeEST } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Briefcase, Inbox, Loader2, RotateCcw, Trash2, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/trash/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { deletedWorkUnits, loading, restoreWorkUnit, permanentlyDeleteWorkUnit } =
    useDeletedWorkUnits(projectId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {deletedWorkUnits.length === 0 ? (
        <EmptyState />
      ) : (
        deletedWorkUnits.map((workUnit) => (
          <WorkUnitCard
            key={workUnit.id}
            workUnit={workUnit}
            projectId={projectId}
            onRestore={restoreWorkUnit}
            onPermanentlyDelete={permanentlyDeleteWorkUnit}
          />
        ))
      )}
    </div>
  );
}

interface WorkUnitCardProps {
  workUnit: any;
  projectId: string;
  onRestore: (workUnitId: string) => Promise<void>;
  onPermanentlyDelete: (workUnitId: string) => Promise<void>;
}

function WorkUnitCard({ workUnit, onRestore, onPermanentlyDelete }: WorkUnitCardProps) {
  const [isRestoring, setIsRestoring] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const confirm = useConfirm();

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore work unit",
      description: `"${workUnit.name}" will be restored to the project.`,
    });

    if (!confirmed) return;

    setIsRestoring(true);
    try {
      await onRestore(workUnit.id);
    } catch (error) {
      console.error("Failed to restore work unit", error);
    } finally {
      setIsRestoring(false);
    }
  };

  const handlePermanentlyDelete = async () => {
    const confirmed = await confirm({
      title: "Permanently delete work unit",
      description: `"${workUnit.name}" will be permanently deleted. This action cannot be undone.`,
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await onPermanentlyDelete(workUnit.id);
    } catch (error) {
      console.error("Failed to permanently delete work unit", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const attachedWorkerCount = workUnit.attachedWorkers?.length || 0;

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            {workUnit.name}
          </CardTitle>
          {workUnit.description && (
            <p className="text-muted-foreground text-sm">{workUnit.description}</p>
          )}
        </div>
        <CardAction className="flex gap-2">
          <Button
            onClick={handleRestore}
            size="sm"
            variant="outline"
            disabled={isRestoring || isDeleting}
          >
            {isRestoring ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <RotateCcw className="size-4" />
                Restore
              </>
            )}
          </Button>
          <Button
            onClick={handlePermanentlyDelete}
            size="sm"
            variant="destructive"
            disabled={isRestoring || isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="size-4" />
                Delete
              </>
            )}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <InfoBlock
          icon={<Briefcase className="text-muted-foreground size-4" />}
          label="Work Type"
          value={workUnit.name}
        />
        <InfoBlock
          icon={<Users className="text-muted-foreground size-4" />}
          label="Workers Needed"
          value={`${attachedWorkerCount} / ${workUnit.workersNeeded}`}
        />
        {workUnit.deletedAt && (
          <InfoBlock
            icon={<Trash2 className="text-muted-foreground size-4" />}
            label="Deleted At"
            value={formatTimeEST(workUnit.deletedAt)}
          />
        )}
      </CardContent>
      <CardFooter className="text-muted-foreground flex-col items-start gap-2 text-xs md:flex-row md:items-center md:justify-between">
        <p>
          {attachedWorkerCount > 0
            ? `This work unit has ${attachedWorkerCount} attached worker${attachedWorkerCount > 1 ? "s" : ""}. Restoring will bring back all assignments.`
            : "Restoring this work unit will add it back to the project."}
        </p>
      </CardFooter>
    </Card>
  );
}

interface InfoBlockProps {
  icon: ReactNode;
  label: string;
  value: string;
}

function InfoBlock({ icon, label, value }: InfoBlockProps) {
  return (
    <div className="border-muted flex flex-col gap-1 rounded-lg border border-dashed p-4">
      <span className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
        {icon}
        {label}
      </span>
      <span className="text-foreground text-sm font-medium">{value}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted work units</p>
        <p className="text-muted-foreground text-sm">
          Recently deleted work units will appear here for quick recovery.
        </p>
      </div>
    </div>
  );
}
